import React, { useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoginForm from '../components/LoginForm';
import './LoginPage.css';

const DEFAULT_AUTH_REDIRECT = '/my/accounts';

function normalizeInternalRedirect(candidate) {
  if (typeof candidate !== 'string') return DEFAULT_AUTH_REDIRECT;

  const value = candidate.trim();
  if (!value) return DEFAULT_AUTH_REDIRECT;

  // Allow only app-internal routes
  if (!value.startsWith('/')) return DEFAULT_AUTH_REDIRECT;
  if (value.startsWith('//')) return DEFAULT_AUTH_REDIRECT;

  // Block protocol/host injection attempts in encoded/decoded forms
  const decoded = decodeURIComponent(value);
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(decoded)) return DEFAULT_AUTH_REDIRECT;

  // Prevent login loop
  if (decoded.startsWith('/login')) return DEFAULT_AUTH_REDIRECT;

  return decoded;
}

export default function LoginPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const next = searchParams.get('next');
    const savedRedirect = sessionStorage.getItem('redirectAfterLogin');
    const stateFrom = location.state?.from;

    return normalizeInternalRedirect(
      stateFrom || next || savedRedirect || DEFAULT_AUTH_REDIRECT
    );
  }, [location.search, location.state]);

  useEffect(() => {
    if (!isAuthenticated) return;

    sessionStorage.removeItem('redirectAfterLogin');
    navigate(from, { replace: true });
  }, [from, isAuthenticated, navigate]);

  if (isAuthenticated) return null;

  return (
    <div className="login-page">
      <div className="card login-page__card">
        <h2 className="login-page__title">Login</h2>

        <LoginForm
          submitLabel="Sign in"
          onSuccess={() => {
            sessionStorage.removeItem('redirectAfterLogin');
            navigate(from, { replace: true });
          }}
        />
        <p className="login-page__links">
          Forgot your password? <Link to="/forgot-password">Reset it</Link>
        </p>
        <p className="login-page__links">
          Need an account? <Link to="/signup">Create one</Link>
        </p>
      </div>
    </div>
  );
}
